import { z } from 'zod';
import { TenantIdSchema, IsoDateSchema } from './_primitives.js';

/**
 * BookingFlow — PYLON-1-MODULE-SCAFFOLD
 *
 * 4-state booking state machine for dock appointment negotiation.
 * Patent gate: F-PYLON-BOOKINGFLOW-001
 */

export const BOOKING_STATES = Object.freeze({
  DRAFT:       'draft',
  NEGOTIATING: 'negotiating',
  CONFIRMED:   'confirmed',
  VOIDED:      'voided',
});

export const BOOKING_TRANSITIONS = Object.freeze({
  draft:       ['negotiating', 'voided'],
  negotiating: ['confirmed', 'draft', 'voided'],
  confirmed:   ['voided'],
  voided:      [],
});

/**
 * Returns true if the transition from → to is permitted.
 * @param {string} fromState
 * @param {string} toState
 * @returns {boolean}
 */
export function isValidTransition(fromState, toState) {
  const allowed = BOOKING_TRANSITIONS[fromState];
  if (!allowed) return false;
  return allowed.includes(toState);
}

const BookingStateEnum = z.enum(Object.values(BOOKING_STATES));

const DraftPayloadSchema = z.object({
  dockId:            z.string().min(1),
  carrierId:         z.string().min(1),
  proposedTime:      z.union([IsoDateSchema, z.string()]),
  estimatedDuration: z.number().positive(),
});

const NegotiationRoundSchema = z.object({
  actor:         z.string(),
  timestamp:     z.union([IsoDateSchema, z.string()]),
  action:        z.string(),
  proposedTime:  z.union([IsoDateSchema, z.string()]).optional(),
  proposedRate:  z.number().optional(),
  note:          z.string().optional(),
}).passthrough();

export const BookingFlowSchema = z.object({
  id:                   z.string().optional(),
  tenantId:             TenantIdSchema,
  appointmentId:        z.string().min(1),
  state:                BookingStateEnum,
  draftPayload:         DraftPayloadSchema,
  negotiationHistory:   z.array(NegotiationRoundSchema).optional(),
  confirmedTime:        z.union([IsoDateSchema, z.string()]).optional().nullable(),
  confirmedRate:        z.number().optional().nullable(),
  rateFloor:            z.number(),
  createdAt:            z.union([IsoDateSchema, z.object({})]).optional(),
  updatedAt:            z.union([IsoDateSchema, z.object({})]).optional(),
  createdBy:            z.string().optional(),
  voidReason:           z.string().optional().nullable(),
  softDeleted:          z.boolean().optional(),
}).passthrough();

/** @typedef {import('zod').infer<typeof BookingFlowSchema>} BookingFlow */
